import clsx from 'clsx';

import { EstimatorInfoTooltip } from './EstimatorInfoTooltip';

interface ChipOption {
  description?: string;
  label: string;
  tooltip?: string;
  value: string;
}

interface Props {
  errorMessage?: string;
  fieldName?: string;
  label: string;
  onToggle: (value: string) => void;
  options: ChipOption[];
  selectedValues: string[];
}

const handleKeyDown =
  (onToggle: (value: string) => void, value: string) =>
  (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle(value);
    }
  };

export const EstimatorChipGroup: React.FC<Props> = ({
  errorMessage,
  fieldName,
  label,
  onToggle,
  options,
  selectedValues,
}) => (
  <div data-estimator-field={fieldName}>
    <div className="mb-3 text-md font-bold text-primary uppercase ml-3">{label}</div>
    <div className="grid auto-rows-fr gap-3 sm:grid-cols-2">
      {options.map(item => {
        const isSelected = selectedValues.includes(item.value);

        return (
          <div key={item.value} className="relative h-full">
            <div
              aria-pressed={isSelected}
              className={clsx(
                'flex h-full w-full cursor-pointer items-center justify-between rounded-[18px] border px-4 py-4 pr-6 text-left transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary/60',
                {
                  'border-primary bg-primary/10': isSelected,
                  'border-white/10 bg-white/5 hover:border-white/30': !isSelected,
                },
              )}
              role="button"
              tabIndex={0}
              onClick={() => onToggle(item.value)}
              onKeyDown={handleKeyDown(onToggle, item.value)}>
              <div className="pr-8 self-start">
                <div className="flex justify-between md:justify-start items-start gap-2 text-white font-medium">
                  {item.label}
                  {!!item.tooltip && <EstimatorInfoTooltip content={item.tooltip} />}
                </div>
                {!!item.description && (
                  <div className="mt-1 pr-3 text-xs leading-6 text-white/62">
                    {item.description}
                  </div>
                )}
              </div>
              <div
                className={clsx('h-[20px] w-[20px] min-w-[20px] rounded-sm border', {
                  'border-primary bg-primary': isSelected,
                  'border-white/50': !isSelected,
                })}
              />
            </div>
          </div>
        );
      })}
    </div>
    {!!errorMessage && <div className="ml-2 mt-1.5 text-xs text-red-600">{errorMessage}</div>}
  </div>
);
